import { appAlert } from '@/lib/appAlert';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/Button';
import { fontSize, radius, shadow, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  captureIdPhoto,
  uploadIdPhoto,
  submitIdentityVerification,
  getMyIdentityVerification,
} from '@/lib/identityVerification';
import { useFocusEffect } from 'expo-router';
import { useCallback } from 'react';
import type { IdentityVerification } from '@/lib/identityVerification';

type Step = 'consent' | 'front' | 'back' | 'selfie' | 'bill' | 'uploading' | 'done';

type CapturedPhoto = { uri: string; base64: string; ext: string; mimeType: string };

export default function IdentityVerifyScreen() {
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { t } = useTranslation();
  const { user } = useAuth();
  const router = useRouter();
  const insets = useSafeAreaInsets();

  const STEPS: { key: 'front' | 'back' | 'selfie' | 'bill'; title: string; hint: string; icon: string }[] = [
    {
      key: 'front',
      title: t('identityVerify.step1_title'),
      hint: t('identityVerify.step1_hint'),
      icon: 'card-outline',
    },
    {
      key: 'back',
      title: t('identityVerify.step2_title'),
      hint: t('identityVerify.step2_hint'),
      icon: 'card-outline',
    },
    {
      key: 'selfie',
      title: t('identityVerify.step3_title'),
      hint: t('identityVerify.step3_hint'),
      icon: 'person-outline',
    },
    {
      key: 'bill',
      title: t('identityVerify.step4_title'),
      hint: t('identityVerify.step4_hint'),
      icon: 'document-text-outline',
    },
  ];

  const [step, setStep] = useState<Step>('consent');
  const [photos, setPhotos] = useState<Partial<Record<'front' | 'back' | 'selfie' | 'bill', CapturedPhoto>>>({});
  const [existing, setExisting] = useState<IdentityVerification | null>(null);
  const [loadingExisting, setLoadingExisting] = useState(true);

  useFocusEffect(
    useCallback(() => {
      if (!user) return;
      getMyIdentityVerification(user.id)
        .then((v) => setExisting(v))
        .catch(() => {})
        .finally(() => setLoadingExisting(false));
    }, [user]),
  );

  const currentStepIndex = step === 'front' ? 0 : step === 'back' ? 1 : step === 'selfie' ? 2 : step === 'bill' ? 3 : -1;
  const currentStepDef = currentStepIndex >= 0 ? STEPS[currentStepIndex] : null;

  async function handleCapture() {
    if (!currentStepDef) return;
    try {
      const photo = await captureIdPhoto(
        step === 'selfie' ? 'front' : 'back',
        step === 'selfie' ? 'selfie' : step === 'bill' ? 'bill' : 'id',
      );
      if (!photo) return;
      setPhotos((prev) => ({ ...prev, [currentStepDef.key]: photo }));
    } catch (e: unknown) {
      console.error('[identity-verify] capture error:', e);
      const msg = e instanceof Error ? e.message : (e as { message?: string })?.message ?? t('identityVerify.captureError_default');
      appAlert(t('common.error'), msg);
    }
  }

  function handleNext() {
    if (step === 'front') setStep('back');
    else if (step === 'back') setStep('selfie');
    else if (step === 'selfie') setStep('bill');
    else if (step === 'bill') void handleSubmit();
  }

  async function handleSubmit() {
    if (!user) return;
    const { front, back, selfie, bill } = photos as Record<string, CapturedPhoto | undefined>;
    if (!front || !back || !selfie) return;
    setStep('uploading');
    try {
      const uploads: Promise<string>[] = [
        uploadIdPhoto(user.id, 'front', front.base64, front.ext, front.mimeType),
        uploadIdPhoto(user.id, 'back', back.base64, back.ext, back.mimeType),
        uploadIdPhoto(user.id, 'selfie', selfie.base64, selfie.ext, selfie.mimeType),
      ];
      if (bill) uploads.push(uploadIdPhoto(user.id, 'bill', bill.base64, bill.ext, bill.mimeType));
      const [fp, bp, sp, billP] = await Promise.all(uploads);
      await submitIdentityVerification(user.id, fp, bp, sp, billP);
      setStep('done');
    } catch (e: unknown) {
      console.error('[identity-verify] upload/submit error:', e);
      const msg = e instanceof Error ? e.message : (e as { message?: string })?.message ?? t('identityVerify.submitError_default');
      appAlert(t('common.error'), msg);
      setStep('bill');
    }
  }

  if (loadingExisting) {
    return (
      <View style={[styles.loader, { paddingTop: insets.top }]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <ScrollView
      style={styles.flex}
      contentContainerStyle={[
        styles.content,
        { paddingTop: insets.top + spacing.lg, paddingBottom: insets.bottom + spacing.xl },
      ]}
    >
      <View style={styles.topBar}>
        <Pressable onPress={() => router.back()} hitSlop={12} accessibilityRole="button">
          <Text style={styles.back}>{t('common.back')}</Text>
        </Pressable>
        <Text style={styles.title}>{t('identityVerify.title')}</Text>
        <View style={{ width: 60 }} />
      </View>

      {/* ── Existing status ── */}
      {existing && step === 'consent' && (
        <View style={[styles.card, existing.status === 'approved' && styles.cardSuccess, existing.status === 'rejected' && styles.cardDanger]}>
          <Ionicons
            name={existing.status === 'approved' ? 'checkmark-circle' : existing.status === 'pending' ? 'time-outline' : 'close-circle'}
            size={32}
            color={existing.status === 'approved' ? colors.success : existing.status === 'pending' ? colors.primary : colors.danger}
          />
          <Text style={styles.statusTitle}>
            {existing.status === 'approved'
              ? t('identityVerify.status_verified')
              : existing.status === 'pending'
              ? t('identityVerify.status_pending')
              : t('identityVerify.status_rejected')}
          </Text>
          {existing.status === 'rejected' && (
            <Text style={styles.statusSub}>{t('identityVerify.resubmit')}</Text>
          )}
          {existing.status !== 'rejected' && (
            <Button title={t('common.back')} variant="secondary" onPress={() => router.back()} />
          )}
        </View>
      )}

      {/* ── Consent screen ── */}
      {step === 'consent' && (!existing || existing.status === 'rejected') && (
        <View style={styles.card}>
          <View style={styles.iconRow}>
            <Ionicons name="shield-checkmark" size={40} color={colors.primary} />
          </View>
          <Text style={styles.consentTitle}>{t('identityVerify.consent_title')}</Text>
          <View style={styles.bullets}>
            {[
              t('identityVerify.consentBullet_photos'),
              t('identityVerify.consentBullet_usage'),
              t('identityVerify.consentBullet_deletion'),
              t('identityVerify.consentBullet_noSharing'),
            ].map((txt, i) => (
              <View key={i} style={styles.bullet}>
                <Ionicons name="checkmark-circle-outline" size={18} color={colors.primary} style={{ marginTop: 2 }} />
                <Text style={styles.bulletText}>{txt}</Text>
              </View>
            ))}
          </View>
          <Button title={t('identityVerify.action_start')} icon="camera-outline" onPress={() => setStep('front')} />
          <Pressable onPress={() => router.back()} style={styles.cancelBtn} accessibilityRole="button">
            <Text style={styles.cancelText}>{t('common.cancel')}</Text>
          </Pressable>
        </View>
      )}

      {/* ── Photo step ── */}
      {currentStepDef && step !== 'uploading' && (
        <View style={styles.card}>
          <View style={styles.stepIndicator}>
            {STEPS.map((s, i) => (
              <View
                key={s.key}
                style={[
                  styles.stepDot,
                  i <= currentStepIndex && styles.stepDotActive,
                ]}
              />
            ))}
          </View>
          <Text style={styles.stepTitle}>
            {t('identityVerify.step', { step: currentStepIndex + 1, title: currentStepDef.title })}
          </Text>
          <Text style={styles.stepHint}>{currentStepDef.hint}</Text>

          {photos[currentStepDef.key] ? (
            <View style={styles.previewWrap}>
              <Image source={{ uri: photos[currentStepDef.key]!.uri }} style={styles.preview} resizeMode="cover" />
              <Pressable onPress={handleCapture} style={styles.retakeBtn} accessibilityRole="button">
                <Ionicons name="camera-outline" size={16} color={colors.primary} />
                <Text style={styles.retakeText}>{t('common.retake')}</Text>
              </Pressable>
            </View>
          ) : (
            <Button title={t('identityVerify.action_takePhoto')} icon="camera-outline" onPress={handleCapture} />
          )}

          {photos[currentStepDef.key] && (
            <Button
              title={step === 'bill' ? t('common.send') : t('common.next')}
              icon={step === 'bill' ? 'cloud-upload-outline' : 'arrow-forward-outline'}
              onPress={handleNext}
            />
          )}
          {step === 'bill' && !photos.bill && (
            <Pressable onPress={handleNext} style={styles.cancelBtn} accessibilityRole="button">
              <Text style={styles.cancelText}>{t('identityVerify.action_skipBill')}</Text>
            </Pressable>
          )}
        </View>
      )}

      {/* ── Uploading ── */}
      {step === 'uploading' && (
        <View style={[styles.card, styles.centeredCard]}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.uploadingText}>{t('identityVerify.uploading')}</Text>
          <Text style={styles.uploadingHint}>{t('identityVerify.uploadingHint')}</Text>
        </View>
      )}

      {/* ── Done ── */}
      {step === 'done' && (
        <View style={[styles.card, styles.cardSuccess, styles.centeredCard]}>
          <Ionicons name="checkmark-circle" size={56} color={colors.success} />
          <Text style={styles.doneTitle}>{t('identityVerify.done_title')}</Text>
          <Text style={styles.doneSub}>{t('identityVerify.done_body')}</Text>
          <Button title={t('common.done')} onPress={() => router.back()} />
        </View>
      )}
    </ScrollView>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: colors.background },
    loader: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.background },
    content: { paddingHorizontal: spacing.lg, gap: spacing.md },
    topBar: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: spacing.sm,
    },
    back: { color: colors.primaryLight, fontSize: fontSize.md, fontWeight: '600', width: 60 },
    title: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700' },
    card: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
      gap: spacing.sm,
      ...shadow.card,
    },
    cardSuccess: { borderColor: colors.success },
    cardDanger: { borderColor: colors.danger },
    centeredCard: { alignItems: 'center' },
    iconRow: { alignItems: 'center' },
    consentTitle: { color: colors.text, fontSize: fontSize.md, fontWeight: '700', textAlign: 'center' },
    bullets: { gap: spacing.sm },
    bullet: { flexDirection: 'row', gap: spacing.sm, alignItems: 'flex-start' },
    bulletText: { color: colors.textMuted, fontSize: fontSize.sm, lineHeight: 20, flex: 1 },
    cancelBtn: { alignSelf: 'center', paddingVertical: spacing.xs },
    cancelText: { color: colors.textMuted, fontSize: fontSize.sm },
    stepIndicator: { flexDirection: 'row', gap: spacing.sm, justifyContent: 'center' },
    stepDot: {
      width: 10,
      height: 10,
      borderRadius: 5,
      backgroundColor: colors.border,
    },
    stepDotActive: { backgroundColor: colors.primary },
    stepTitle: { color: colors.text, fontSize: fontSize.md, fontWeight: '700', textAlign: 'center' },
    stepHint: { color: colors.textMuted, fontSize: fontSize.sm, lineHeight: 20, textAlign: 'center' },
    previewWrap: { gap: spacing.xs },
    preview: {
      width: '100%',
      height: 200,
      borderRadius: radius.md,
      backgroundColor: colors.surfaceAlt,
    },
    retakeBtn: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, alignSelf: 'flex-end' },
    retakeText: { color: colors.primary, fontSize: fontSize.sm, fontWeight: '600' },
    statusTitle: { color: colors.text, fontSize: fontSize.md, fontWeight: '700', textAlign: 'center' },
    statusSub: { color: colors.textMuted, fontSize: fontSize.sm, textAlign: 'center' },
    uploadingText: { color: colors.text, fontSize: fontSize.md, fontWeight: '700', textAlign: 'center' },
    uploadingHint: { color: colors.textMuted, fontSize: fontSize.sm, textAlign: 'center' },
    doneTitle: { color: colors.success, fontSize: fontSize.lg, fontWeight: '800', textAlign: 'center' },
    doneSub: { color: colors.textMuted, fontSize: fontSize.sm, lineHeight: 20, textAlign: 'center' },
  });
}
